import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { toast } from "sonner";
import { formatApiError } from "../lib/api";
import SEOHead from "../components/SEOHead";

const TABS = [
  { id: "signin", label: "Sign in" },
  { id: "signup", label: "Sign up" },
];

export default function Login() {
  const { login, verifyTwoFactor, register } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [params] = useSearchParams();
  const redirect = params.get("redirect") || "/";

  // Default tab from query param OR route (/register defaults to signup)
  const initialTab =
    params.get("tab") === "signup" || location.pathname === "/register"
      ? "signup"
      : "signin";
  const [tab, setTab] = useState(initialTab);
  useEffect(() => { setTab(initialTab); }, [initialTab]);

  const [signin, setSignin] = useState({ email: "", password: "" });
  const [signup, setSignup] = useState({ name: "", email: "", phone: "", password: "" });
  const [submitting, setSubmitting] = useState(false);
  // iter46: 2-step login state
  const [twoFA, setTwoFA] = useState(null); // { ephemeralToken, email } or null
  const [otp, setOtp] = useState("");

  const onSignin = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const result = await login(signin.email, signin.password);
      if (result?.twoFactorRequired) {
        setTwoFA({ ephemeralToken: result.ephemeralToken, email: signin.email });
        toast.message("Enter your 6-digit authenticator code");
        return;
      }
      toast.success("Welcome back!");
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onVerify2FA = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const data = await verifyTwoFactor(twoFA.ephemeralToken, otp);
      if (data?.recovery_code_used) {
        toast.message("Recovery code used — generate fresh codes after sign-in.");
      } else {
        toast.success("Welcome back!");
      }
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const onSignup = async (e) => {
    e.preventDefault();
    if (signup.password.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await register(signup);
      toast.success("Welcome to Sri Durga Foods!");
      navigate(redirect);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const googleLogin = () => {
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="container-x py-16 lg:py-24 max-w-md" data-testid="auth-page">
      <SEOHead
        title={tab === "signin" ? "Sign in | Sri Durga Foods" : "Create your account | Sri Durga Foods"}
        description="Access your Sri Durga Foods account to place orders, track shipments, and manage saved addresses."
        noindex
      />
      <div className="text-center mb-10">
        <span className="overline mb-3 block">
          {tab === "signin" ? "Welcome back" : "Join the family"}
        </span>
        <h1 className="font-serif text-4xl text-brand-dark">
          {tab === "signin" ? "Sign in to your account" : "Create your account"}
        </h1>
      </div>

      <div className="bg-white rounded-2xl border border-brand-line overflow-hidden">
        {/* Tab strip */}
        <div className="grid grid-cols-2 border-b border-brand-line" data-testid="auth-tabs">
          {TABS.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => setTab(t.id)}
              className={`py-4 text-sm font-medium tracking-wide transition-colors ${
                tab === t.id
                  ? "text-brand-chili border-b-2 border-brand-chili bg-brand-cream/40"
                  : "text-brand-muted active:text-brand-chili"
              }`}
              data-testid={`auth-tab-${t.id}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="p-8">
          {twoFA ? (
            <form onSubmit={onVerify2FA} className="space-y-5" data-testid="signin-2fa-form">
              <div className="text-center mb-2">
                <h3 className="font-serif text-xl text-brand-dark mb-1">Two-factor authentication</h3>
                <p className="text-xs text-brand-muted">
                  Enter the 6-digit code from your authenticator app, or a one-time recovery code.
                </p>
              </div>
              <input
                type="text"
                inputMode="text"
                maxLength={20}
                autoFocus
                className="input-field font-mono text-center text-2xl tracking-widest"
                placeholder="000000"
                value={otp}
                onChange={(e) => setOtp(e.target.value.trim())}
                data-testid="login-2fa-otp"
              />
              <button type="submit" disabled={submitting || !otp} className="btn-primary w-full" data-testid="login-2fa-submit">
                {submitting ? "Verifying…" : "Verify and sign in"}
              </button>
              <div className="text-center text-sm">
                <button type="button" onClick={() => { setTwoFA(null); setOtp(""); }}
                  className="text-brand-chili hover:underline">Use a different account</button>
              </div>
            </form>
          ) : tab === "signin" ? (
            <form onSubmit={onSignin} className="space-y-5" data-testid="signin-form">
              <div>
                <label className="label-field">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={signin.email}
                  onChange={(e) => setSignin({ ...signin, email: e.target.value })}
                  required
                  autoComplete="email"
                  data-testid="login-email"
                />
              </div>
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <label className="label-field !mb-0">Password</label>
                  <button
                    type="button"
                    onClick={() => {
                      const e = signin.email.trim();
                      if (!e) {
                        toast.error("Please enter your email above first.");
                        return;
                      }
                      navigate("/reset-password", { state: { email: e } });
                    }}
                    className="text-xs font-medium text-brand-chili active:text-brand-chili/80 [@media(hover:hover)]:hover:text-brand-chili/80 transition-colors"
                    data-testid="forgot-password-link"
                  >
                    Forgot password?
                  </button>
                </div>
                <input
                  type="password"
                  className="input-field"
                  value={signin.password}
                  onChange={(e) => setSignin({ ...signin, password: e.target.value })}
                  required
                  autoComplete="current-password"
                  data-testid="login-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="login-submit"
              >
                {submitting ? "Signing in…" : "Sign in"}
              </button>
            </form>
          ) : (
            <form onSubmit={onSignup} className="space-y-5" data-testid="signup-form">
              <div>
                <label className="label-field">Name</label>
                <input
                  className="input-field"
                  value={signup.name}
                  onChange={(e) => setSignup({ ...signup, name: e.target.value })}
                  required
                  autoComplete="name"
                  data-testid="register-name"
                />
              </div>
              <div>
                <label className="label-field">Email</label>
                <input
                  type="email"
                  className="input-field"
                  value={signup.email}
                  onChange={(e) => setSignup({ ...signup, email: e.target.value })}
                  required
                  autoComplete="email"
                  data-testid="register-email"
                />
              </div>
              <div>
                <label className="label-field">Phone (optional)</label>
                <input
                  className="input-field"
                  value={signup.phone}
                  onChange={(e) => setSignup({ ...signup, phone: e.target.value })}
                  inputMode="tel"
                  autoComplete="tel"
                  data-testid="register-phone"
                />
              </div>
              <div>
                <label className="label-field">Password</label>
                <input
                  type="password"
                  className="input-field"
                  value={signup.password}
                  onChange={(e) => setSignup({ ...signup, password: e.target.value })}
                  required
                  minLength={6}
                  autoComplete="new-password"
                  data-testid="register-password"
                />
              </div>
              <button
                type="submit"
                disabled={submitting}
                className="btn-primary w-full"
                data-testid="register-submit"
              >
                {submitting ? "Creating…" : "Create account"}
              </button>
            </form>
          )}

          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-brand-line" />
            </div>
            <span className="relative bg-white px-4 text-xs uppercase tracking-widest text-brand-muted block w-fit mx-auto">or</span>
          </div>

          <button
            type="button"
            onClick={googleLogin}
            className="btn-secondary w-full flex items-center justify-center gap-2"
            data-testid="login-google"
          >
            <svg width="18" height="18" viewBox="0 0 18 18" aria-hidden="true">
              <path fill="#4285F4" d="M17.64 9.2c0-.64-.06-1.25-.17-1.84H9v3.48h4.84a4.14 4.14 0 01-1.79 2.71v2.26h2.9c1.7-1.57 2.69-3.88 2.69-6.61z"/>
              <path fill="#34A853" d="M9 18c2.43 0 4.47-.8 5.96-2.18l-2.9-2.26c-.81.54-1.83.86-3.06.86-2.35 0-4.34-1.59-5.05-3.72H.96v2.33A9 9 0 009 18z"/>
              <path fill="#FBBC05" d="M3.95 10.7A5.4 5.4 0 013.65 9c0-.6.1-1.17.3-1.7V4.97H.96A9 9 0 000 9c0 1.45.35 2.83.96 4.03l2.99-2.33z"/>
              <path fill="#EA4335" d="M9 3.58c1.32 0 2.5.45 3.44 1.35l2.58-2.58A8.99 8.99 0 009 0 9 9 0 00.96 4.97L3.95 7.3C4.66 5.17 6.65 3.58 9 3.58z"/>
            </svg>
            Continue with Google
          </button>
        </div>
      </div>

      <p className="text-sm text-center text-brand-muted mt-6">
        {tab === "signin" ? (
          <>
            New here?{" "}
            <button
              type="button"
              onClick={() => setTab("signup")}
              className="text-brand-chili font-medium"
              data-testid="auth-switch-to-signup"
            >
              Create an account
            </button>
          </>
        ) : (
          <>
            Already have an account?{" "}
            <button
              type="button"
              onClick={() => setTab("signin")}
              className="text-brand-chili font-medium"
              data-testid="auth-switch-to-signin"
            >
              Sign in
            </button>
          </>
        )}
      </p>
    </div>
  );
}
